import { type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "../i18n";

type RecordKey = `trip:${string}` | `lodging:${string}`;

const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const PING_INTERVAL_MS = 25_000;
const RECONNECT_INVALIDATE_DEBOUNCE_MS = 5_000;

const TOAST_KEYS: Record<
  string,
  | "realtime.trip.amended"
  | "realtime.trip.withdrawn"
  | "realtime.trip.undone"
  | "realtime.trip.created"
  | "realtime.lodging.amended"
  | "realtime.lodging.withdrawn"
  | "realtime.lodging.undone"
  | "realtime.lodging.created"
  | "realtime.hours.amended"
  | "realtime.hours.rejected"
  | "realtime.hours.unchecked"
> = {
  "trip.amended": "realtime.trip.amended",
  "trip.withdrawn": "realtime.trip.withdrawn",
  "trip.undone": "realtime.trip.undone",
  "trip.created": "realtime.trip.created",
  "lodging.amended": "realtime.lodging.amended",
  "lodging.withdrawn": "realtime.lodging.withdrawn",
  "lodging.undone": "realtime.lodging.undone",
  "lodging.created": "realtime.lodging.created",
  "hours.amended": "realtime.hours.amended",
  "hours.rejected": "realtime.hours.rejected",
  "hours.unchecked": "realtime.hours.unchecked",
};

export class RealtimeClient {
  #ws: WebSocket | null = null;
  #queryClient: QueryClient | null = null;
  #closed = false;
  #attempt = 0;
  #pingTimer: ReturnType<typeof setInterval> | null = null;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #toastTimer: ReturnType<typeof setTimeout> | null = null;
  #lastInboxInvalidateAt = 0;

  constructor(private url: string) {}

  connect(queryClient: QueryClient, _userId: string): void {
    this.#queryClient = queryClient;
    this.#closed = false;
    this.#open();
  }

  disconnect(): void {
    this.#closed = true;
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    if (this.#pingTimer) clearInterval(this.#pingTimer);
    this.#ws?.close();
    this.#ws = null;
  }

  presenceEnter(recordKey: RecordKey): void {
    this.#send({ type: "presence.enter", recordKey });
  }

  presenceLeave(recordKey: RecordKey): void {
    this.#send({ type: "presence.leave", recordKey });
  }

  #open(): void {
    const ws = new WebSocket(this.url);
    this.#ws = ws;
    ws.onopen = () => {
      this.#attempt = 0;
      this.#pingTimer = setInterval(() => this.#send({ type: "ping" }), PING_INTERVAL_MS);
      // Debounce inbox refetches across reconnect storms. Oscillating networks
      // would otherwise refetch trip + lodging declarations per reconnect.
      const now = Date.now();
      if (now - this.#lastInboxInvalidateAt >= RECONNECT_INVALIDATE_DEBOUNCE_MS) {
        this.#lastInboxInvalidateAt = now;
        this.#queryClient?.invalidateQueries({ queryKey: ["trip-declarations"] });
        this.#queryClient?.invalidateQueries({ queryKey: ["lodging-declarations"] });
      }
    };
    ws.onmessage = (ev) => this.#dispatch(ev.data);
    ws.onclose = () => this.#onClose();
    ws.onerror = () => this.#onClose();
  }

  #onClose(): void {
    if (this.#pingTimer) {
      clearInterval(this.#pingTimer);
      this.#pingTimer = null;
    }
    if (this.#closed) return;
    const delay = BACKOFF_MS[Math.min(this.#attempt, BACKOFF_MS.length - 1)]!;
    this.#attempt += 1;
    this.#reconnectTimer = setTimeout(() => this.#open(), delay);
  }

  #send(msg: unknown): void {
    if (this.#ws?.readyState === 1) this.#ws.send(JSON.stringify(msg));
  }

  #dispatch(data: string): void {
    const qc = this.#queryClient;
    if (!qc) return;
    let event: { type: string; [k: string]: unknown };
    try {
      event = JSON.parse(data);
    } catch {
      return;
    }

    switch (event.type) {
      case "pong":
        return;
      case "auth_refresh_required":
        this.disconnect();
        qc.invalidateQueries({ queryKey: ["session"] });
        return;
      case "trip.amended":
      case "trip.created":
      case "trip.withdrawn":
      case "trip.undone": {
        qc.invalidateQueries({ queryKey: ["trip-declarations"] });
        qc.invalidateQueries({ queryKey: ["change-log"] });
        this.#queueToast(event.type);
        return;
      }
      case "lodging.amended":
      case "lodging.created":
      case "lodging.withdrawn":
      case "lodging.undone": {
        qc.invalidateQueries({ queryKey: ["lodging-declarations"] });
        qc.invalidateQueries({ queryKey: ["change-log"] });
        this.#queueToast(event.type);
        return;
      }
      case "trip.undo_window_open":
      case "lodging.undo_window_open": {
        const id = (event["tripId"] ?? event["lodgingId"]) as string;
        const expiresAt = event["expiresAt"] as number;
        const ms = Math.max(0, expiresAt - Date.now());
        toast(i18n.t("realtime.undo.label"), {
          duration: ms,
          action: {
            label: i18n.t("realtime.undo.action"),
            onClick: () => this.#undo(event.type.startsWith("trip"), id),
          },
        });
        return;
      }
      case "trip.presence": {
        qc.setQueryData(["presence", `trip:${event["tripId"]}`], event["userIds"]);
        return;
      }
      case "lodging.presence": {
        qc.setQueryData(["presence", `lodging:${event["lodgingId"]}`], event["userIds"]);
        return;
      }
      case "hours.amended":
      case "hours.rejected":
      case "hours.unchecked": {
        qc.invalidateQueries({ queryKey: ["hours-entries"] });
        qc.invalidateQueries({ queryKey: ["me"] });
        qc.invalidateQueries({ queryKey: ["change-log"] });
        this.#queueToast(event.type);
        return;
      }
      // admin.* events are silent: invalidate caches, no toast.
      case "admin.week_submitted":
      case "admin.hours_changed": {
        qc.invalidateQueries({ queryKey: ["hours-entries"] });
        return;
      }
      case "admin.trip_changed": {
        qc.invalidateQueries({ queryKey: ["trip-declarations"] });
        return;
      }
      case "admin.lodging_changed": {
        qc.invalidateQueries({ queryKey: ["lodging-declarations"] });
        return;
      }
    }
  }

  #queueToast(eventType: string): void {
    if (this.#toastTimer) clearTimeout(this.#toastTimer);
    const key = TOAST_KEYS[eventType];
    if (!key) return;
    this.#toastTimer = setTimeout(() => {
      toast(i18n.t(key));
    }, 1_000);
  }

  async #undo(isTrip: boolean, id: string): Promise<void> {
    const path = isTrip
      ? `/api/v1/trip-declarations/${id}/undo`
      : `/api/v1/lodging-declarations/${id}/undo`;
    await fetch(path, { method: "POST", credentials: "include" });
  }
}
